import { useState, useEffect, useMemo } from 'react';
import { message } from 'antd';
import { useTranslate } from '@tolgee/react';

import { calculatePackagePrice } from 'services/api/pay';
import { find } from 'lodash-es';

import { PackageCheckboxProps, PackageSelectPanel } from './PackageSelectPanel';
import { DurationSelectPanel } from './DurationSelectPanel';

/** 连续包月（自动续费订阅）时长项的 id */
const SUBSCRIPTION_DURATION_ID = 5 as Pay.DurationId;

export interface PackageDetailProps {
  orgId: number;
  packageInfo: Pay.PackageInfoV2;
  currency: General.Currency;
  isEnterprise: boolean;
  setAccountNum: React.Dispatch<React.SetStateAction<number>>;
  setDurationNum: React.Dispatch<React.SetStateAction<Pay.DurationNum | -1>>;
  setCanOrder: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEnterprise: React.Dispatch<React.SetStateAction<boolean>>;
  setIsSubscription?: React.Dispatch<React.SetStateAction<boolean>>;
  onPackageChange?: PackageCheckboxProps['onChange'];
  onCalculatePackageChange?: (pkg?: Pay.CalculatePackagePriceRst) => void;
}

function PackageDetail(props: PackageDetailProps) {
  const {
    packageInfo,
    setAccountNum,
    setDurationNum,
    setCanOrder,
    setIsEnterprise,
    setIsSubscription,
    orgId,
    currency,
    isEnterprise,
    onPackageChange,
    onCalculatePackageChange,
  } = props;
  const { packageData, recommendData } = packageInfo;

  const { recommendPackageId, recommendDurationId } = recommendData;
  // 选中的套餐
  const [chkPackage, setChkPackage] = useState<Pay.PackageId | undefined>(recommendPackageId);
  // 默认选中连续包月（自动续费订阅）
  const [chkDuration, setChkDuration] = useState<Pay.DurationId>(SUBSCRIPTION_DURATION_ID);

  // 选中套餐的时长
  const durationDesc = useMemo(() => {
    const checkedPackage = find(packageData, ['id', chkPackage]);
    return checkedPackage?.durationDesc ?? [];
  }, [packageData, chkPackage]);

  const { t } = useTranslate();

  const injectedDurationDesc = useMemo(() => {
    if (!durationDesc.length) return durationDesc;
    const subscriptionItem: Pay.DurationDescV2 = {
      id: SUBSCRIPTION_DURATION_ID,
      name: t('连续包月'),
      durationNum: -1,
      subscription: true,
      price: 0,
      currencyAmount: [],
    };
    // 连续包月放在「一个月」和「一季度」中间（即索引 1）
    return [...durationDesc.slice(0, 1), subscriptionItem, ...durationDesc.slice(1)];
  }, [durationDesc, t]);

  // 重新计算价格
  useEffect(() => {
    const checkedDuration = find(injectedDurationDesc, ['id', chkDuration]);
    if (checkedDuration && chkPackage) {
      const { durationNum } = checkedDuration;
      calculatePackagePrice({
        orgId,
        orderType: 'ORDER',
        durationNum,
        newPackageId: chkPackage,
        currency,
      })
        .then((d) => {
          setDurationNum(durationNum);
          setCanOrder(true);
          onCalculatePackageChange?.(d);
        })
        .catch((e) => {
          setCanOrder(false);
          onCalculatePackageChange?.(undefined);
          message.error(`/calculatePackagePrice ${e?.message}`);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chkDuration, chkPackage, currency, injectedDurationDesc]);

  return (
    <>
      <PackageSelectPanel
        value={chkPackage}
        recommendPackageId={recommendPackageId}
        list={packageData}
        isEnterprise={isEnterprise}
        onChange={(isEnterprise, selected) => {
          if (isEnterprise) {
            // 企业版不支持自动续费
            setIsSubscription?.(false);
            setIsEnterprise(true);
            setChkPackage(undefined);
          } else if (selected) {
            const { accountNum, id } = selected;
            setAccountNum(accountNum);
            setChkPackage(id);
            setChkDuration(SUBSCRIPTION_DURATION_ID);
            setIsSubscription?.(true);
            setIsEnterprise(false);
          }
          onPackageChange?.(isEnterprise, selected);
        }}
      />
      {injectedDurationDesc.length ? (
        <DurationSelectPanel
          durationId={chkDuration}
          packageId={chkPackage}
          currency={currency}
          list={injectedDurationDesc}
          packageData={packageData}
          recommendDurationId={recommendDurationId}
          onChange={({ durationNum, id, subscription }) => {
            setDurationNum(durationNum);
            setChkDuration(id);
            setIsSubscription?.(!!subscription);
          }}
        />
      ) : null}
    </>
  );
}

export default PackageDetail;
